import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, TextField, InputAdornment, Checkbox, Chip, Avatar,
  Select, MenuItem, FormControl, Slider, Switch, Snackbar, Alert, CircularProgress,
  ToggleButtonGroup, ToggleButton, Divider, Tooltip, IconButton, alpha,
  Dialog, DialogContent,
} from '@mui/material';
import {
  Search as SearchIcon, Edit as EditIcon, AutoAwesome as SmartIcon,
  CalendarMonth as CalIcon, AccessTime as TimeIcon, Bolt as BoltIcon,
  ChevronLeft, ChevronRight, Send as SendIcon, DoneAll as DoneAllIcon,
  AddPhotoAlternate as AddPhotoIcon, Tag as TagIcon, Link as LinkIcon,
  Repeat as RepeatIcon, VisibilityOff as SpoilerIcon, HelpOutline as HelpIcon,
  LibraryBooks as LibraryIcon, SaveOutlined as SaveIcon, Close as CloseIcon,
  ChatBubbleOutline as ReplyIcon, ScheduleSend as ScheduleIcon, AutoFixHigh as GhostIcon,
  PushPin as PinIcon, CheckCircle as CheckCircleIcon, Visibility as VisibilityIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { designTokens as t } from '../theme';
import { getToken } from '../api';
import MediaPicker from '../components/MediaPicker';
import ThumbStack from '../components/ThumbStack';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8080';
const VARIABLES = ['username', 'name', 'day_en', 'day_fr', 'p_firstname', 'p_age', 'p_origin', 'p_location', 'p_occupation'];
const WD = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

const metaOf = (s) => (s && s.Data && s.Data.meta) || {};
const pad = (n) => String(n).padStart(2, '0');
const dateKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const GROUP_COLORS = ['#6366f1', '#06b6d4', '#f59e0b', '#ec4899', '#10b981', '#8b5cf6', '#ef4444', '#14b8a6'];

export default function CreatePost() {
  const [sessions, setSessions] = useState([]);
  const [groups, setGroups] = useState({});
  const [captions, setCaptions] = useState([]);
  const [toast, setToast] = useState({ open: false, msg: '', sev: 'success' });
  const notify = (msg, sev = 'success') => setToast({ open: true, msg, sev });

  // selection
  const [groupFilter, setGroupFilter] = useState('all');
  const [accountSearch, setAccountSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [tab, setTab] = useState('smart');

  // smart config
  const [mode, setMode] = useState('random');
  const [captionSource, setCaptionSource] = useState('group');
  // sélection explicite de légendes (modale "Select captions"). Vide = on retombe
  // sur le comportement "Source des légendes" (par groupe / toutes).
  const [selectedCaptions, setSelectedCaptions] = useState(new Set());
  const [captionModalOpen, setCaptionModalOpen] = useState(false);
  const [libraryLoaderOpen, setLibraryLoaderOpen] = useState(false);
  const [boost, setBoost] = useState(false);
  const [boostMinLikes, setBoostMinLikes] = useState(10);
  // sticky : un post unique imposé sur tous les comptes sélectionnés
  const [sticky, setSticky] = useState(false);
  const [stickyPost, setStickyPost] = useState(null); // {message, mediaType, imageUploadIds, preview, source}
  const [stickyModalOpen, setStickyModalOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [selectedDates, setSelectedDates] = useState(new Set());
  const [activeStart, setActiveStart] = useState(8);
  const [activeEnd, setActiveEnd] = useState(22);
  const [postsPerDay, setPostsPerDay] = useState(5);
  const [postsMode, setPostsMode] = useState('exact'); // 'exact' | 'range'
  const [postsMin, setPostsMin] = useState(3);
  const [postsMax, setPostsMax] = useState(5);
  const [minInterval, setMinInterval] = useState(30);
  const [scheduling, setScheduling] = useState(false);

  // basic config
  const basicRef = useRef(null);
  const basicFileRef = useRef(null);
  const [basicText, setBasicText] = useState('');
  const [basicImages, setBasicImages] = useState([]); // {id, preview}
  const [basicUploading, setBasicUploading] = useState(false);
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
  const [randomSource, setRandomSource] = useState(null); // {folder, count, name}

  const handleMediaAdd = ({ uploads, random }) => {
    if (uploads && uploads.length) {
      setRandomSource(null);
      setBasicImages((prev) => [...prev, ...uploads]);
    }
    if (random) {
      setBasicImages([]); // random remplace les images fixes
      setRandomSource(random);
    }
  };
  const [topic, setTopic] = useState('');
  const [link, setLink] = useState('');
  const [whoCanReply, setWhoCanReply] = useState('everyone');
  const [mediaSpoiler, setMediaSpoiler] = useState(false);
  const [ghostPost, setGhostPost] = useState(false);
  const [repeatSchedule, setRepeatSchedule] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [basicDate, setBasicDate] = useState(() => { const d = new Date(Date.now() + 3600000); return d.toISOString().slice(0, 16); });

  const fetchAll = useCallback(async () => {
    try {
      const [s, g, d] = await Promise.all([
        axios.get(`${API}/api/sessions`),
        axios.get(`${API}/api/profiles/config`).catch(() => ({ data: { profiles: {} } })),
        axios.get(`${API}/api/drafts`).catch(() => ({ data: { drafts: [] } })),
      ]);
      setSessions(Array.isArray(s.data) ? s.data : []);
      setGroups(g.data?.profiles || {});
      setCaptions(Array.isArray(d.data?.drafts) ? d.data.drafts : []);
    } catch (e) { notify('Erreur de chargement', 'error'); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const groupList = useMemo(() => Object.entries(groups).map(([id, g], i) => ({
    id, name: g.name || id, color: g.color || GROUP_COLORS[i % GROUP_COLORS.length],
  })), [groups]);
  const groupColor = useCallback((profile) => groupList.find((g) => g.name === profile || g.id === profile)?.color, [groupList]);
  // Normalise une valeur de groupe (id OU nom) vers une clé canonique (nom en minuscule).
  const canonGroup = useCallback((val) => {
    if (!val) return '';
    const s = String(val).toLowerCase();
    const g = groupList.find((x) => x.id.toLowerCase() === s || x.name.toLowerCase() === s);
    return g ? g.name.toLowerCase() : s;
  }, [groupList]);

  const visibleAccounts = useMemo(() => sessions.filter((s) => {
    const meta = metaOf(s);
    if (groupFilter !== 'all' && (s.Profile || meta.profile) !== groupFilter) return false;
    if (accountSearch.trim()) {
      const q = accountSearch.toLowerCase();
      if (!`${s.Name} ${meta.username || ''} ${meta.full_name || ''}`.toLowerCase().includes(q)) return false;
    }
    return true;
  }), [sessions, groupFilter, accountSearch]);

  const allVisibleSelected = visibleAccounts.length > 0 && visibleAccounts.every((s) => selected.has(s.Name));
  const toggleSelectAll = () => {
    const n = new Set(selected);
    if (allVisibleSelected) visibleAccounts.forEach((s) => n.delete(s.Name));
    else visibleAccounts.forEach((s) => n.add(s.Name));
    setSelected(n);
  };
  const toggleAccount = (name) => setSelected((prev) => { const n = new Set(prev); n.has(name) ? n.delete(name) : n.add(name); return n; });

  // caption pool count (for the header badge)
  const captionPoolCount = useMemo(() => {
    if (captionSource === 'all') return captions.length;
    // group mode: count distinct captions across selected accounts' groups
    const grps = new Set([...selected].map((name) => {
      const s = sessions.find((x) => x.Name === name); return s ? canonGroup(s.Profile || metaOf(s).profile) : null;
    }).filter(Boolean));
    if (grps.size === 0) return captions.length;
    // une légende compte si son groupe (résolu) correspond à un groupe sélectionné
    return captions.filter((c) => grps.has(canonGroup(c.profile))).length;
  }, [captionSource, captions, selected, sessions, canonGroup]);

  // Dernier sticky déjà programmé parmi les comptes sélectionnés (meta.sticky.post) :
  // permet de "reprendre" le même sticky pour prolonger une programmation sans le refaire.
  const lastSticky = useMemo(() => {
    let best = null; let bestAt = -1;
    selected.forEach((name) => {
      const s = sessions.find((x) => x.Name === name);
      if (!s) return;
      const st = metaOf(s).sticky;
      if (!st || typeof st !== 'object' || !st.post) return;
      const at = Number(st.activatedAt) || 0;
      if (at >= bestAt) { bestAt = at; best = { post: st.post, account: metaOf(s).username || name }; }
    });
    if (!best) return null;
    const p = best.post;
    const ids = Array.isArray(p.imageUploadIds) ? p.imageUploadIds : [];
    return {
      account: best.account,
      post: {
        message: p.message || '',
        mediaType: p.mediaType || (ids.length ? 'image' : 'text'),
        imageUploadIds: ids,
        preview: ids.length ? `${API}/api/uploads/${ids[0]}/raw?token=${getToken()}` : null,
        source: 'previous',
        ...(p.randomFolder ? { randomFolder: p.randomFolder, randomCount: p.randomCount } : {}),
      },
    };
  }, [selected, sessions]);

  // ---- calendar ----
  const calendarDays = useMemo(() => {
    const first = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
    const startDow = first.getDay();
    const daysInMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(viewMonth.getFullYear(), viewMonth.getMonth(), d));
    return cells;
  }, [viewMonth]);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const toggleDate = (d) => {
    if (d < today) return;
    const k = dateKey(d);
    setSelectedDates((prev) => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });
  };
  // Sélectionne les N prochains jours à partir d'aujourd'hui (aujourd'hui inclus).
  const selectNextDays = (n) => {
    const next = new Set();
    for (let i = 0; i < n; i++) {
      const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() + i);
      next.add(dateKey(d));
    }
    setSelectedDates(next);
    // recentre le calendrier sur aujourd'hui pour voir la sélection
    setViewMonth(new Date(today.getFullYear(), today.getMonth(), 1));
  };

  // ---- schedule ----
  const handleSmartSchedule = async () => {
    if (selected.size === 0) { notify('Sélectionne au moins un compte', 'error'); return; }
    if (selectedDates.size === 0) { notify('Sélectionne au moins une date', 'error'); return; }
    if (sticky && !stickyPost) { notify('Sticky activé : choisis un post', 'error'); return; }
    setScheduling(true);
    try {
      const hasCaptionSelection = selectedCaptions.size > 0;
      const { data } = await axios.post(`${API}/api/scheduled-posts/bulk-smart`, {
        accounts: [...selected],
        mode,
        // Sélection explicite → chaque compte pioche dans la sélection (source "all").
        // Sinon on garde la source configurée (par groupe / toutes).
        captionSource: hasCaptionSelection ? 'all' : captionSource,
        captionIds: hasCaptionSelection ? [...selectedCaptions] : [],
        dates: [...selectedDates],
        activeStart, activeEnd,
        postsPerDay, minIntervalMin: minInterval,
        postsMode, postsMin, postsMax,
        boost, boostMinLikes: Number(boostMinLikes) || 0,
        sticky,
        stickyPost: sticky && stickyPost ? {
          message: stickyPost.message || '',
          mediaType: stickyPost.mediaType || 'text',
          imageUploadIds: stickyPost.imageUploadIds || [],
          ...(stickyPost.randomFolder ? { randomFolder: stickyPost.randomFolder, randomCount: stickyPost.randomCount } : {}),
        } : null,
      });
      const parts = [];
      if (data.sticky) parts.push(`${data.sticky} en sticky 📌`);
      if (data.boosted) parts.push(`${data.boosted} boosté(s)`);
      if (data.boostFallbackAccounts) parts.push(`${data.boostFallbackAccounts} compte(s) sans critère boost → random`);
      if (data.skippedAccounts) parts.push(`${data.skippedAccounts} compte(s) sans légende ignoré(s)`);
      notify(`${data.created} posts programmés${parts.length ? ` · ${parts.join(' · ')}` : ''}`);
      setSelectedDates(new Set());
    } catch (e) {
      notify(e.response?.data?.error || 'Erreur de planification', 'error');
    } finally {
      setScheduling(false);
    }
  };

  // Charge un post de la Post Library dans le composeur Basic (texte + images).
  const loadPostFromLibrary = (post) => {
    setBasicText((post.message || '').slice(0, 500));
    const ids = post.imageUploadIds || post.uploadIds || [];
    if (Array.isArray(ids) && ids.length) {
      setRandomSource(null);
      setBasicImages(ids.map((id) => ({ id, preview: `${API}/api/uploads/${id}/raw?token=${getToken()}` })));
    } else {
      setBasicImages([]);
    }
    setLibraryLoaderOpen(false);
    notify('Post chargé depuis la Library');
  };

  const insertVar = (key) => {
    const el = basicRef.current;
    const token = `{${key}}`;
    if (!el) { setBasicText((m) => m + token); return; }
    const start = el.selectionStart ?? basicText.length;
    const end = el.selectionEnd ?? basicText.length;
    setBasicText(basicText.slice(0, start) + token + basicText.slice(end));
    requestAnimationFrame(() => { el.focus(); el.selectionStart = el.selectionEnd = start + token.length; });
  };

  const handleBasicUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setBasicUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('image', file);
        const { data } = await axios.post(`${API}/api/upload`, fd);
        setBasicImages((prev) => [...prev, { id: data.uploadId, preview: URL.createObjectURL(file) }]);
      }
    } catch (err) { notify("Erreur d'upload", 'error'); }
    finally { setBasicUploading(false); }
  };

  // Compose le message final : topic (#) + texte + lien.
  const buildMessage = () => {
    let msg = basicText;
    if (topic.trim()) msg = `#${topic.trim().replace(/^#/, '')}\n${msg}`;
    if (link.trim()) msg = `${msg}\n${link.trim()}`;
    return msg;
  };

  const basicPayload = (name, extra = {}) => ({
    id: `sched_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    session: name,
    message: buildMessage(),
    mediaType: (basicImages.length || randomSource) ? 'image' : 'text',
    uploadIds: basicImages.map((i) => i.id),
    ...(randomSource ? { randomFolder: randomSource.folder, randomCount: randomSource.count } : {}),
    ...extra,
  });

  const handleBasicPostNow = async () => {
    if (selected.size === 0) { notify('Sélectionne au moins un compte', 'error'); return; }
    if (!basicText.trim() && basicImages.length === 0 && !randomSource) { notify('Le post est vide', 'error'); return; }
    setScheduling(true);
    try {
      const iso = new Date().toISOString();
      let ok = 0;
      for (const name of selected) {
        try {
          const { data } = await axios.post(`${API}/api/scheduled-posts`, basicPayload(name, { scheduledTime: iso }));
          const id = data.post?.id || data.post?.ID;
          if (id) axios.post(`${API}/api/scheduled-posts/${id}/publish-now`).catch(() => {});
          ok++;
        } catch (e) { /* skip */ }
      }
      notify(`Publication lancée sur ${ok} compte(s)`);
    } catch (e) { notify('Erreur', 'error'); }
    finally { setScheduling(false); }
  };

  const handleBasicSchedule = async () => {
    if (selected.size === 0) { notify('Sélectionne au moins un compte', 'error'); return; }
    if (!basicText.trim() && basicImages.length === 0 && !randomSource) { notify('Le post est vide', 'error'); return; }
    setScheduling(true);
    try {
      const iso = new Date(basicDate).toISOString();
      let ok = 0;
      for (const name of selected) {
        try {
          await axios.post(`${API}/api/scheduled-posts`, basicPayload(name, { scheduledTime: iso, repeatDaily: repeatSchedule }));
          ok++;
        } catch (e) { /* skip */ }
      }
      notify(`${ok} post(s) programmé(s)${repeatSchedule ? ' (quotidien)' : ''}`);
      setShowSchedule(false);
    } catch (e) { notify('Erreur', 'error'); }
    finally { setScheduling(false); }
  };

  const handleSaveDraft = async () => {
    if (!basicText.trim() && basicImages.length === 0 && !randomSource) { notify('Rien à enregistrer', 'error'); return; }
    try {
      await axios.post(`${API}/api/drafts`, {
        message: buildMessage(),
        profile: groupFilter !== 'all' ? groupFilter : '',
        imageUploadIds: basicImages.map((i) => i.id),
        mediaType: basicImages.length ? 'image' : 'text',
        title: `Post ${new Date().toLocaleString()}`,
      });
      notify('Brouillon enregistré dans la Post Library');
    } catch (e) { notify('Erreur enregistrement', 'error'); }
  };

  const rangeLo = Math.min(postsMin, postsMax);
  const rangeHi = Math.max(postsMin, postsMax);
  const perDayLabel = postsMode === 'range' ? (rangeLo === rangeHi ? `${rangeLo}` : `${rangeLo}–${rangeHi}`) : `${postsPerDay}`;
  const avgPerDay = postsMode === 'range' ? (rangeLo + rangeHi) / 2 : postsPerDay;
  const estimatedTotal = Math.round(selected.size * selectedDates.size * avgPerDay);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 1.5 }}>
        <Box>
          <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.08em', color: t.mutedFaint, textTransform: 'uppercase' }}>Compose</Typography>
          <Typography sx={{ fontSize: '1.6rem', fontWeight: 700, letterSpacing: '-0.02em' }}>Create Post</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Chip icon={<Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#34d399', ml: 1 }} />} label={`${sessions.length} comptes`} sx={{ bgcolor: t.card, border: `1px solid ${t.border}` }} />
          <Chip icon={<Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: t.primary, ml: 1 }} />} label={`${groupList.length} groupes`} sx={{ bgcolor: t.card, border: `1px solid ${t.border}` }} />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start', flexWrap: { xs: 'wrap', lg: 'nowrap' } }}>
        {/* ===== ACCOUNTS panel ===== */}
        <Box sx={{ width: { xs: '100%', lg: 300 }, flexShrink: 0, border: `1px solid ${t.border}`, borderRadius: 2.5, bgcolor: t.card, p: 2 }}>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.08em', color: t.mutedFaint, textTransform: 'uppercase', mb: 1.5 }}>Accounts</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 1 }}>
            <Select value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)} sx={{ bgcolor: t.background }}>
              <MenuItem value="all">Tous les groupes</MenuItem>
              {groupList.map((g) => <MenuItem key={g.id} value={g.name}>{g.name}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField fullWidth size="small" placeholder="Rechercher…" value={accountSearch} onChange={(e) => setAccountSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment> }} sx={{ mb: 1, '& .MuiOutlinedInput-root': { bgcolor: t.background } }} />
          <Button fullWidth variant="outlined" color="inherit" startIcon={<DoneAllIcon sx={{ fontSize: 18 }} />} onClick={toggleSelectAll}
            sx={{ borderColor: t.border, color: t.muted, mb: 1.5 }}>
            {allVisibleSelected ? 'Tout désélectionner' : `Tout sélectionner (${visibleAccounts.length})`}
          </Button>
          <Box sx={{ maxHeight: 480, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {visibleAccounts.map((s) => {
              const meta = metaOf(s);
              const handle = meta.username || s.Name;
              const col = groupColor(s.Profile || meta.profile);
              const isSel = selected.has(s.Name);
              return (
                <Box key={s.Name} onClick={() => toggleAccount(s.Name)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.75, borderRadius: 1.5, cursor: 'pointer',
                    bgcolor: isSel ? alpha(t.primary, 0.1) : 'transparent', border: `1px solid ${isSel ? alpha(t.primary, 0.3) : 'transparent'}`,
                    '&:hover': { bgcolor: isSel ? alpha(t.primary, 0.12) : alpha('#fff', 0.03) } }}>
                  <Checkbox size="small" checked={isSel} sx={{ p: 0 }} />
                  <Avatar src={meta.profile_pic_url || undefined} sx={{ width: 26, height: 26, fontSize: '0.75rem', bgcolor: alpha(t.primary, 0.2), color: '#c7d2fe' }}>
                    {(handle || '?').charAt(0).toUpperCase()}
                  </Avatar>
                  <Typography noWrap sx={{ flex: 1, fontSize: '0.82rem' }}>@{handle}</Typography>
                  <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: col || t.mutedFaint }} />
                </Box>
              );
            })}
            {visibleAccounts.length === 0 && <Typography sx={{ fontSize: '0.8rem', color: t.mutedFaint, py: 2, textAlign: 'center' }}>Aucun compte</Typography>}
          </Box>
        </Box>

        {/* ===== main panel ===== */}
        <Box sx={{ flex: 1, minWidth: 0, border: `1px solid ${t.border}`, borderRadius: 2.5, bgcolor: t.card, p: 2.5 }}>
          {/* tabs */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 1 }}>
            <ToggleButtonGroup exclusive size="small" value={tab} onChange={(_, v) => v && setTab(v)}
              sx={{ bgcolor: t.background, p: 0.4, borderRadius: 2, '& .MuiToggleButton-root': { border: 0, borderRadius: '6px !important', px: 1.75, py: 0.5, textTransform: 'none', color: t.muted, gap: 0.5 }, '& .Mui-selected': { bgcolor: `${alpha(t.primary, 0.18)} !important`, color: '#c7d2fe !important' } }}>
              <ToggleButton value="basic"><EditIcon sx={{ fontSize: 16 }} /> Basic</ToggleButton>
              <ToggleButton value="smart"><SmartIcon sx={{ fontSize: 16 }} /> Smart Post</ToggleButton>
            </ToggleButtonGroup>
            <Typography sx={{ fontSize: '0.78rem', color: t.mutedFaint }}>
              <b style={{ color: t.foreground }}>{selected.size}</b> compte(s) sélectionné(s)
            </Typography>
          </Box>

          {tab === 'basic' ? (
            /* ---------- BASIC ---------- */
            <Box>
              <Box sx={{ display: 'flex', gap: 2.5, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                {/* editor */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ border: `1px solid ${t.border}`, borderRadius: 2.5, bgcolor: t.background, p: 1.5 }}>
                    <TextField inputRef={basicRef} fullWidth multiline minRows={6} maxRows={16} variant="standard"
                      placeholder="Quoi de neuf ?" value={basicText}
                      onChange={(e) => setBasicText(e.target.value.slice(0, 500))}
                      InputProps={{ disableUnderline: true, sx: { fontSize: '0.95rem' } }} />
                    {basicImages.length > 0 && (
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                        {basicImages.map((img, i) => (
                          <Box key={i} sx={{ position: 'relative' }}>
                            <Box component="img" src={img.preview} sx={{ width: 60, height: 60, borderRadius: 1.5, objectFit: 'cover', border: `1px solid ${t.border}` }} />
                            <IconButtonClose onClick={() => setBasicImages((p) => p.filter((_, idx) => idx !== i))} />
                          </Box>
                        ))}
                      </Box>
                    )}
                    <Divider sx={{ my: 1.25, borderColor: t.border }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: t.muted }}>
                        <input ref={basicFileRef} type="file" accept="image/*" multiple hidden onChange={handleBasicUpload} />
                        <Tooltip title="Ajouter des images ou vidéos">
                          <Box component="span" onClick={() => setMediaPickerOpen(true)} sx={iconBtn}>
                            <AddPhotoIcon sx={{ fontSize: 19 }} />
                          </Box>
                        </Tooltip>
                        {randomSource && (
                          <Chip size="small" label={`🎲 Random : ${randomSource.count} de "${randomSource.name}"`} onDelete={() => setRandomSource(null)}
                            sx={{ bgcolor: alpha('#f43f5e', 0.15), color: '#fca5a5', border: '1px solid rgba(244,63,94,0.3)' }} />
                        )}
                      </Box>
                      <Typography sx={{ fontSize: '0.78rem', color: basicText.length > 450 ? '#fbbf24' : t.mutedFaint }}>{500 - basicText.length}</Typography>
                    </Box>
                  </Box>

                  {/* variables */}
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      <Typography sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.78rem', color: t.mutedFaint }}>{'{}'}</Typography>
                      <Typography sx={{ fontSize: '0.78rem', color: t.muted, fontWeight: 600 }}>Variables</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      {VARIABLES.map((v) => (
                        <Chip key={v} label={v} size="small" onClick={() => insertVar(v)}
                          sx={{ bgcolor: alpha('#fff', 0.04), border: `1px solid ${t.border}`, color: t.muted, fontFamily: 'JetBrains Mono, monospace', fontSize: '0.72rem', cursor: 'pointer', '&:hover': { bgcolor: alpha(t.primary, 0.15), color: '#c7d2fe' } }} />
                      ))}
                    </Box>
                    <Typography sx={{ fontSize: '0.72rem', color: t.mutedFaint, mt: 1 }}>Résolues automatiquement selon le compte cible.</Typography>
                  </Box>
                </Box>

                {/* options column */}
                <Box sx={{ width: { xs: '100%', md: 280 }, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Label icon={<TagIcon sx={{ fontSize: 15 }} />} text="Threads topic" />
                    <TextField fullWidth size="small" placeholder="topic" value={topic} onChange={(e) => setTopic(e.target.value)}
                      InputProps={{ startAdornment: <InputAdornment position="start"><TagIcon sx={{ fontSize: 16 }} /></InputAdornment>, sx: { bgcolor: t.background } }} />
                  </Box>
                  <Box>
                    <Label icon={<LinkIcon sx={{ fontSize: 15 }} />} text="Lien (ajouté au texte)" help />
                    <TextField fullWidth size="small" placeholder="https://example.com" value={link} onChange={(e) => setLink(e.target.value)}
                      InputProps={{ sx: { bgcolor: t.background } }} />
                  </Box>
                  <Box>
                    <Label icon={<ReplyIcon sx={{ fontSize: 15 }} />} text="Qui peut répondre" />
                    <FormControl fullWidth size="small">
                      <Select value={whoCanReply} onChange={(e) => setWhoCanReply(e.target.value)} sx={{ bgcolor: t.background }} disabled>
                        <MenuItem value="everyone">Tout le monde</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <OptionToggle icon={<SpoilerIcon sx={{ fontSize: 18 }} />} title="Media Spoiler" sub="Ajoute un média pour activer" checked={mediaSpoiler} onChange={setMediaSpoiler} disabled />
                  <OptionToggle icon={<GhostIcon sx={{ fontSize: 18 }} />} title="Ghost Post" sub="Disparaît après 24h (bientôt)" checked={ghostPost} onChange={setGhostPost} disabled />
                  <OptionToggle icon={<RepeatIcon sx={{ fontSize: 18 }} />} title="Répéter chaque jour" sub="Republie à la même heure" checked={repeatSchedule} onChange={setRepeatSchedule} />
                </Box>
              </Box>

              {/* footer */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2.5, pt: 2, borderTop: `1px solid ${t.border}`, flexWrap: 'wrap' }}>
                <Button size="small" color="inherit" startIcon={<LibraryIcon sx={{ fontSize: 18 }} />} onClick={() => setLibraryLoaderOpen(true)} sx={{ color: t.muted }}>Library</Button>
                <Button size="small" color="inherit" startIcon={<SaveIcon sx={{ fontSize: 18 }} />} onClick={handleSaveDraft} sx={{ color: t.muted }}>Save draft</Button>
                <Box sx={{ flex: 1 }} />
                {showSchedule && (
                  <TextField type="datetime-local" size="small" value={basicDate} onChange={(e) => setBasicDate(e.target.value)}
                    InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { bgcolor: t.background } }} />
                )}
                <Button variant="outlined" color="inherit" startIcon={<ScheduleIcon sx={{ fontSize: 18 }} />}
                  onClick={() => showSchedule ? handleBasicSchedule() : setShowSchedule(true)} disabled={scheduling}
                  sx={{ borderColor: t.border, color: t.muted }}>
                  {showSchedule ? 'Confirmer' : 'Schedule'}
                </Button>
                <Button variant="contained" startIcon={<SendIcon sx={{ fontSize: 18 }} />} onClick={handleBasicPostNow} disabled={scheduling}>
                  {scheduling ? <CircularProgress size={20} /> : `Post to ${selected.size} accounts`}
                </Button>
              </Box>
            </Box>
          ) : (
            /* ---------- SMART POST ---------- */
            <Box sx={{ display: 'flex', gap: 2.5, flexWrap: { xs: 'wrap', xl: 'nowrap' } }}>
              {/* captions + mode */}
              <Box sx={{ flex: 1, minWidth: 240 }}>
                {/* Barre "Select captions" : ouvre la modale Post Library pour
                    choisir un sous-ensemble précis de légendes. */}
                <Box role="button" onClick={() => setCaptionModalOpen(true)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, borderRadius: 2, cursor: 'pointer',
                    bgcolor: alpha(t.primary, 0.08), border: `1px solid ${alpha(t.primary, 0.2)}`, mb: 2,
                    transition: 'background .15s', '&:hover': { bgcolor: alpha(t.primary, 0.14) } }}>
                  <LibraryIcon sx={{ fontSize: 18, color: t.primary }} />
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, flex: 1 }}>
                    {selectedCaptions.size > 0 ? `${selectedCaptions.size} légende(s) sélectionnée(s)` : 'Select captions'}
                  </Typography>
                  {selectedCaptions.size > 0 && (
                    <Chip label={selectedCaptions.size} size="small" sx={{ bgcolor: alpha(t.primary, 0.2), color: '#c7d2fe', fontWeight: 700 }} />
                  )}
                </Box>

                {selectedCaptions.size > 0 ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Typography sx={{ fontSize: '0.72rem', color: t.mutedFaint, flex: 1 }}>
                      Chaque post tire parmi tes {selectedCaptions.size} légende(s) sélectionnée(s).
                    </Typography>
                    <Button size="small" onClick={() => setSelectedCaptions(new Set())} sx={{ color: t.mutedFaint, textTransform: 'none', fontSize: '0.72rem' }}>
                      Tout désélectionner
                    </Button>
                  </Box>
                ) : (
                  <>
                    <Typography sx={{ fontSize: '0.72rem', color: t.mutedFaint, mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Source des légendes</Typography>
                    <ToggleButtonGroup exclusive fullWidth size="small" value={captionSource} onChange={(_, v) => v && setCaptionSource(v)} sx={tgStyle}>
                      <ToggleButton value="group">Par groupe</ToggleButton>
                      <ToggleButton value="all">Toutes</ToggleButton>
                    </ToggleButtonGroup>
                    <Typography sx={{ fontSize: '0.72rem', color: t.mutedFaint, mt: 0.5, mb: 2 }}>
                      {captionSource === 'group' ? 'Chaque compte tire dans les légendes de son groupe.' : 'Tous les comptes tirent dans toute la library.'}
                    </Typography>
                  </>
                )}

                <Typography sx={{ fontSize: '0.72rem', color: t.mutedFaint, mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Mode</Typography>
                <ToggleButtonGroup exclusive fullWidth size="small" value={mode} onChange={(_, v) => v && setMode(v)} sx={tgStyle}>
                  <ToggleButton value="random">Random</ToggleButton>
                  <ToggleButton value="rotate">Rotate</ToggleButton>
                </ToggleButtonGroup>
                <Typography sx={{ fontSize: '0.72rem', color: t.mutedFaint, mt: 0.5 }}>
                  {mode === 'random' ? 'Chaque post tire une légende au hasard.' : 'Les légendes défilent dans l\'ordre.'}
                </Typography>

                <Divider sx={{ my: 2, borderColor: t.border }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BoltIcon sx={{ fontSize: 18, color: boost ? '#f59e0b' : t.mutedFaint }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Boost</Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: t.mutedFaint }}>Republie les meilleures légendes du compte</Typography>
                  </Box>
                  <Switch checked={boost} onChange={(e) => setBoost(e.target.checked)} />
                </Box>
                {boost && (
                  <Box sx={{ mt: 1.5, pl: 0.5 }}>
                    <Typography sx={{ fontSize: '0.72rem', color: t.mutedFaint, mb: 0.75 }}>
                      Min. likes (posts des 7 derniers jours)
                    </Typography>
                    <TextField
                      type="number" size="small" fullWidth value={boostMinLikes}
                      onChange={(e) => setBoostMinLikes(e.target.value)}
                      inputProps={{ min: 0 }}
                      InputProps={{ startAdornment: <InputAdornment position="start"><BoltIcon sx={{ fontSize: 15, color: '#f59e0b' }} /></InputAdornment> }}
                    />
                    <Typography sx={{ fontSize: '0.7rem', color: t.mutedFaint, mt: 0.75, lineHeight: 1.45 }}>
                      Un compte est boosté seulement s'il a <b>≥ 5 posts</b> et au moins un post des 7 derniers jours
                      avec <b>≥ {Number(boostMinLikes) || 0} likes</b>. Sinon → légende random (boost ignoré pour ce compte).
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 2, borderColor: t.border }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PinIcon sx={{ fontSize: 18, color: sticky ? '#ec4899' : t.mutedFaint }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Sticky post</Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: t.mutedFaint }}>Toujours le même post, sur tous les comptes</Typography>
                  </Box>
                  <Switch checked={sticky} onChange={(e) => setSticky(e.target.checked)} sx={{ '& .Mui-checked': { color: '#ec4899' }, '& .Mui-checked + .MuiSwitch-track': { bgcolor: '#ec4899 !important' } }} />
                </Box>
                {sticky && (
                  <Box sx={{ mt: 1.5, pl: 0.5 }}>
                    {stickyPost ? (
                      <Box sx={{ border: `1px solid ${alpha('#ec4899', 0.35)}`, borderRadius: 2, bgcolor: alpha('#ec4899', 0.06), p: 1.25 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          {(stickyPost.preview) && (
                            <Box component="img" src={stickyPost.preview} sx={{ width: 44, height: 44, borderRadius: 1.5, objectFit: 'cover', border: `1px solid ${t.border}`, flexShrink: 0 }} />
                          )}
                          {!stickyPost.preview && stickyPost.randomFolder && (
                            <Box sx={{ width: 44, height: 44, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha('#f43f5e', 0.15), flexShrink: 0 }}>🎲</Box>
                          )}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: '0.78rem', lineHeight: 1.35, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                              {stickyPost.message || (stickyPost.randomFolder ? `🎲 Image random (${stickyPost.randomCount})` : '(image seule)')}
                            </Typography>
                            <Typography sx={{ fontSize: '0.68rem', color: t.mutedFaint, mt: 0.25 }}>
                              {stickyPost.source === 'library' ? 'Depuis la Library' : 'Post composé'}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.75, mt: 1 }}>
                          <Button size="small" startIcon={<EditIcon sx={{ fontSize: 15 }} />} onClick={() => setStickyModalOpen(true)} sx={{ color: '#f9a8d4', textTransform: 'none', fontSize: '0.72rem' }}>Modifier</Button>
                          <Button size="small" startIcon={<CloseIcon sx={{ fontSize: 15 }} />} onClick={() => setStickyPost(null)} sx={{ color: t.mutedFaint, textTransform: 'none', fontSize: '0.72rem' }}>Retirer</Button>
                        </Box>
                      </Box>
                    ) : (
                      <>
                        {/* Reprendre le sticky déjà programmé (extension de programmation) */}
                        {lastSticky && (
                          <Box onClick={() => setStickyPost(lastSticky.post)}
                            sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, mb: 1, borderRadius: 1.5, cursor: 'pointer',
                              border: `1px dashed ${alpha('#ec4899', 0.5)}`, bgcolor: alpha('#ec4899', 0.06),
                              '&:hover': { bgcolor: alpha('#ec4899', 0.12) } }}>
                            {lastSticky.post.preview
                              ? <Box component="img" src={lastSticky.post.preview} sx={{ width: 36, height: 36, borderRadius: 1, objectFit: 'cover', flexShrink: 0 }} />
                              : <HistoryIcon sx={{ fontSize: 20, color: '#f9a8d4', flexShrink: 0 }} />}
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#f9a8d4' }}>Reprendre le dernier sticky</Typography>
                              <Typography sx={{ fontSize: '0.7rem', color: t.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {lastSticky.post.message || (lastSticky.post.randomFolder ? `🎲 Image random (${lastSticky.post.randomCount})` : '(image seule)')} · @{lastSticky.account}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        <Button fullWidth variant="outlined" startIcon={<PinIcon sx={{ fontSize: 16 }} />} onClick={() => setStickyModalOpen(true)}
                          sx={{ borderColor: alpha('#ec4899', 0.4), color: '#f9a8d4', textTransform: 'none', '&:hover': { borderColor: '#ec4899', bgcolor: alpha('#ec4899', 0.08) } }}>
                          {lastSticky ? 'Choisir un autre post' : 'Choisir le post'}
                        </Button>
                      </>
                    )}
                    <Typography sx={{ fontSize: '0.7rem', color: t.mutedFaint, mt: 0.75, lineHeight: 1.45 }}>
                      Ce post sera publié <b>tel quel à chaque créneau</b>, sur tous les comptes sélectionnés.
                      Sticky est <b>prioritaire</b> sur Boost et la library.
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* calendar */}
              <Box sx={{ width: { xs: '100%', xl: 300 }, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5, color: t.muted }}>
                  <CalIcon sx={{ fontSize: 18 }} /><Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Dates</Typography>
                </Box>
                {/* raccourcis de sélection */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.25 }}>
                  {[['7 prochains j', 7], ['15 prochains j', 15], ['31 prochains j', 31]].map(([label, n]) => (
                    <Box key={n} role="button" onClick={() => selectNextDays(n)}
                      sx={{ px: 1.15, py: 0.5, borderRadius: 1.5, fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', userSelect: 'none',
                        border: `1px solid ${t.border}`, color: t.muted, transition: 'all .12s ease',
                        '&:hover': { borderColor: t.primary, color: t.primary, bgcolor: alpha(t.primary, 0.08) } }}>
                      {label}
                    </Box>
                  ))}
                  {selectedDates.size > 0 && (
                    <Box role="button" onClick={() => setSelectedDates(new Set())}
                      sx={{ px: 1.15, py: 0.5, borderRadius: 1.5, fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', userSelect: 'none',
                        border: `1px solid transparent`, color: t.mutedFaint, '&:hover': { color: t.foreground } }}>
                      Effacer
                    </Box>
                  )}
                </Box>
                <Box sx={{ border: `1px solid ${t.border}`, borderRadius: 2, p: 1.5, bgcolor: t.background }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box role="button" onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1))} sx={{ cursor: 'pointer', color: t.muted, display: 'flex' }}><ChevronLeft /></Box>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>{MONTHS[viewMonth.getMonth()]} {viewMonth.getFullYear()}</Typography>
                    <Box role="button" onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1))} sx={{ cursor: 'pointer', color: t.muted, display: 'flex' }}><ChevronRight /></Box>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
                    {WD.map((w) => <Typography key={w} sx={{ textAlign: 'center', fontSize: '0.68rem', color: t.mutedFaint, py: 0.5 }}>{w}</Typography>)}
                    {calendarDays.map((d, i) => {
                      if (!d) return <Box key={i} />;
                      const k = dateKey(d);
                      const isSel = selectedDates.has(k);
                      const isPast = d < today;
                      const isToday = dateKey(d) === dateKey(today);
                      return (
                        <Box key={i} onClick={() => toggleDate(d)}
                          sx={{ textAlign: 'center', py: 0.6, borderRadius: 1.5, fontSize: '0.8rem', cursor: isPast ? 'not-allowed' : 'pointer',
                            color: isPast ? t.mutedFaint : isSel ? '#fff' : t.foreground, opacity: isPast ? 0.4 : 1,
                            bgcolor: isSel ? t.primary : 'transparent',
                            border: isToday && !isSel ? `1px solid ${t.primary}` : '1px solid transparent',
                            fontWeight: isSel ? 700 : 500, '&:hover': isPast ? {} : { bgcolor: isSel ? t.primaryHover : alpha('#fff', 0.06) } }}>
                          {d.getDate()}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
                <Typography sx={{ fontSize: '0.75rem', color: t.mutedFaint, mt: 1 }}>{selectedDates.size} jour(s) sélectionné(s)</Typography>
              </Box>

              {/* time config */}
              <Box sx={{ width: { xs: '100%', xl: 280 }, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5, color: t.muted }}>
                  <TimeIcon sx={{ fontSize: 18 }} /><Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Configuration horaire</Typography>
                </Box>
                <Box sx={{ border: `1px solid ${t.border}`, borderRadius: 2, p: 2, bgcolor: t.background }}>
                  <Typography sx={{ fontSize: '0.72rem', color: t.mutedFaint, mb: 0.5 }}>Fuseau</Typography>
                  <Chip label="Paris (CET/CEST)" size="small" sx={{ bgcolor: alpha('#fff', 0.05), mb: 2 }} />

                  <Typography sx={{ fontSize: '0.72rem', color: t.mutedFaint, mb: 0.5 }}>Heures actives</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <Select value={activeStart} onChange={(e) => setActiveStart(e.target.value)}>
                        {Array.from({ length: 24 }, (_, h) => <MenuItem key={h} value={h}>{pad(h)}:00</MenuItem>)}
                      </Select>
                    </FormControl>
                    <Typography sx={{ color: t.mutedFaint }}>→</Typography>
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <Select value={activeEnd} onChange={(e) => setActiveEnd(e.target.value)}>
                        {Array.from({ length: 24 }, (_, h) => <MenuItem key={h} value={h}>{pad(h)}:00</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Box>

                  <Box sx={{ p: 1.25, borderRadius: 1.5, bgcolor: alpha(t.primary, 0.08), border: `1px solid ${alpha(t.primary, 0.2)}`, mb: 2 }}>
                    <Typography sx={{ fontSize: '0.74rem', color: '#c7d2fe', fontWeight: 600, mb: 0.25 }}>Distribution aléatoire</Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: t.muted }}>
                      Les {perDayLabel} posts/jour seront répartis aléatoirement entre {pad(activeStart)}:00 et {pad(activeEnd)}:00, avec ≥ {minInterval}min d'écart.
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography sx={{ fontSize: '0.78rem', color: t.muted }}>Posts / jour / compte</Typography>
                    <ToggleButtonGroup exclusive size="small" value={postsMode} onChange={(_, v) => v && setPostsMode(v)} sx={tgStyle}>
                      <ToggleButton value="exact" sx={{ px: 1.25 }}>Exact</ToggleButton>
                      <ToggleButton value="range" sx={{ px: 1.25 }}>Range</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>

                  {postsMode === 'exact' ? (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: t.primary }}>{postsPerDay}</Typography>
                      </Box>
                      <Slider value={postsPerDay} onChange={(_, v) => setPostsPerDay(v)} min={1} max={20} sx={{ mb: 1.5 }} />
                    </>
                  ) : (
                    <Box sx={{ mb: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography sx={{ fontSize: '0.72rem', color: t.mutedFaint }}>Nombre aléatoire entre min et max chaque jour</Typography>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: t.primary }}>{rangeLo}–{rangeHi}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                        <Typography sx={{ fontSize: '0.74rem', color: t.muted }}>Min : <b style={{ color: t.foreground }}>{postsMin}</b></Typography>
                      </Box>
                      <Slider value={postsMin} onChange={(_, v) => { setPostsMin(v); if (v > postsMax) setPostsMax(v); }} min={1} max={20} sx={{ mb: 1 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                        <Typography sx={{ fontSize: '0.74rem', color: t.muted }}>Max : <b style={{ color: t.foreground }}>{postsMax}</b></Typography>
                      </Box>
                      <Slider value={postsMax} onChange={(_, v) => { setPostsMax(v); if (v < postsMin) setPostsMin(v); }} min={1} max={20} />
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography sx={{ fontSize: '0.78rem', color: t.muted }}>Intervalle min</Typography>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: t.primary }}>{minInterval}min</Typography>
                  </Box>
                  <Slider value={minInterval} onChange={(_, v) => setMinInterval(v)} min={5} max={180} step={5} />
                </Box>
              </Box>
            </Box>
          )}

          {/* footer action (smart) */}
          {tab === 'smart' && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2, mt: 2.5, pt: 2, borderTop: `1px solid ${t.border}` }}>
              <Typography sx={{ fontSize: '0.82rem', color: t.mutedFaint }}>
                ≈ <b style={{ color: t.foreground }}>{estimatedTotal}</b> posts ({selected.size} comptes × {selectedDates.size} jours × {perDayLabel})
              </Typography>
              <Button variant="contained" startIcon={<SendIcon sx={{ fontSize: 18 }} />} onClick={handleSmartSchedule} disabled={scheduling}>
                {scheduling ? <CircularProgress size={20} /> : 'Programmer les posts'}
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      <Snackbar open={toast.open} autoHideDuration={4000} onClose={() => setToast((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={toast.sev} variant="filled" onClose={() => setToast((s) => ({ ...s, open: false }))}>{toast.msg}</Alert>
      </Snackbar>

      <MediaPicker open={mediaPickerOpen} onClose={() => setMediaPickerOpen(false)} onAdd={handleMediaAdd} used={basicImages.length} />

      <StickyPicker
        open={stickyModalOpen}
        onClose={() => setStickyModalOpen(false)}
        captions={captions}
        groupColor={groupColor}
        groupList={groupList}
        canonGroup={canonGroup}
        onChoose={(p) => { setStickyPost(p); setStickyModalOpen(false); }}
        notify={notify}
      />

      <CaptionPicker
        open={captionModalOpen}
        onClose={() => setCaptionModalOpen(false)}
        captions={captions}
        groupList={groupList}
        canonGroup={canonGroup}
        initial={selectedCaptions}
        onApply={(ids) => setSelectedCaptions(ids)}
      />

      <LibraryLoader
        open={libraryLoaderOpen}
        onClose={() => setLibraryLoaderOpen(false)}
        captions={captions}
        groupList={groupList}
        canonGroup={canonGroup}
        onLoad={loadPostFromLibrary}
      />
    </Box>
  );
}

// Type de média d'un post de la library.
function mediaTypeOf(p) {
  if (p.mediaType) return p.mediaType;
  if (Array.isArray(p.imageUploadIds) && p.imageUploadIds.length) return 'image';
  if (p.videoData) return 'video';
  return 'text';
}
function fmtLibDate(v) {
  if (!v) return '—';
  const d = new Date(typeof v === 'number' ? (v > 1e12 ? v : v * 1000) : v);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
}

// Modale "Load from Post Library" (bouton Library du Basic) : liste des posts à
// gauche + aperçu à droite ; un clic sur "Charger" injecte le post dans le composeur.
function LibraryLoader({ open, onClose, captions, groupList, canonGroup, onLoad }) {
  const [search, setSearch] = useState('');
  const [grp, setGrp] = useState('all');
  const [typeF, setTypeF] = useState('all'); // all | text | image | video
  const [selId, setSelId] = useState(null);

  useEffect(() => { if (open) { setSearch(''); setGrp('all'); setTypeF('all'); setSelId(null); } }, [open]);

  const capText = (c) => c.message || c.text || '';
  const capThumb = (c) => {
    const ids = c.imageUploadIds || c.uploadIds;
    if (Array.isArray(ids) && ids.length) return `${API}/api/uploads/${ids[0]}/raw?token=${getToken()}`;
    if (c.videoData?.uploadId) return `${API}/api/uploads/${c.videoData.uploadId}/raw?token=${getToken()}`;
    return null;
  };
  const grpInfo = (profile) => groupList.find((g) => canonGroup(g.name) === canonGroup(profile)) || null;

  // compteurs par groupe (pour les onglets)
  const groupCounts = useMemo(() => {
    const m = {};
    captions.forEach((c) => { const k = canonGroup(c.profile); if (k) m[k] = (m[k] || 0) + 1; });
    return m;
  }, [captions, canonGroup]);

  const filtered = useMemo(() => captions.filter((c) => {
    if (grp !== 'all' && canonGroup(c.profile) !== canonGroup(grp)) return false;
    if (typeF !== 'all' && mediaTypeOf(c) !== typeF) return false;
    if (search.trim() && !capText(c).toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [captions, grp, typeF, search, canonGroup]);

  const selected = filtered.find((c) => c.id === selId) || captions.find((c) => c.id === selId) || null;

  const TYPES = [['all', 'All'], ['text', 'Aa'], ['image', 'Img'], ['video', 'Vid']];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { bgcolor: '#0b0b0d', border: `1px solid ${t.border}`, borderRadius: 3, height: '86vh' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 3, py: 2, borderBottom: `1px solid ${t.border}` }}>
        <LibraryIcon sx={{ fontSize: 20, color: t.primary, mr: 1 }} />
        <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, flex: 1 }}>Load from Post Library</Typography>
        <IconButton onClick={onClose} sx={{ color: t.mutedFaint }}><CloseIcon /></IconButton>
      </Box>

      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* ---- left : list ---- */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', borderRight: `1px solid ${t.border}` }}>
          <Box sx={{ px: 2.5, pt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '0.68rem', color: t.mutedFaint, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Library</Typography>
                <Typography sx={{ fontSize: '1.15rem', fontWeight: 700 }}>Post Library</Typography>
                <Chip label={captions.length} size="small" sx={{ bgcolor: alpha(t.primary, 0.2), color: '#c7d2fe', fontWeight: 700, height: 20 }} />
              </Box>
              <TextField size="small" placeholder="Rechercher…" value={search} onChange={(e) => setSearch(e.target.value)}
                sx={{ width: 220 }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: t.mutedFaint }} /></InputAdornment> }} />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              {/* group tabs */}
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                <GroupTab active={grp === 'all'} onClick={() => setGrp('all')} label="Tous" count={captions.length} />
                {groupList.map((g) => (
                  <GroupTab key={g.id} active={canonGroup(grp) === canonGroup(g.name)} onClick={() => setGrp(g.name)}
                    label={g.name} count={groupCounts[canonGroup(g.name)] || 0} color={g.color} />
                ))}
              </Box>
              {/* type filter */}
              <ToggleButtonGroup exclusive size="small" value={typeF} onChange={(_, v) => v && setTypeF(v)} sx={tgStyle}>
                {TYPES.map(([v, l]) => <ToggleButton key={v} value={v} sx={{ px: 1.25 }}>{l}</ToggleButton>)}
              </ToggleButtonGroup>
            </Box>
          </Box>

          {/* column head */}
          <Box sx={{ display: 'flex', alignItems: 'center', px: 2.5, py: 1, borderTop: `1px solid ${t.border}`, borderBottom: `1px solid ${t.border}`,
            color: t.mutedFaint, fontSize: '0.66rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            <Box sx={{ flex: 1 }}>Caption</Box>
            <Box sx={{ width: 90 }}>Groupe</Box>
            <Box sx={{ width: 50, textAlign: 'right' }}>Utilisé</Box>
            <Box sx={{ width: 60, textAlign: 'right' }}>Date</Box>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {filtered.length === 0 ? (
              <Box sx={{ py: 6, textAlign: 'center', color: t.mutedFaint }}>
                <LibraryIcon sx={{ fontSize: 34, opacity: 0.4, mb: 1 }} />
                <Typography sx={{ fontSize: '0.85rem' }}>Aucun post{search || grp !== 'all' || typeF !== 'all' ? ' pour ce filtre' : ' dans la library'}.</Typography>
              </Box>
            ) : filtered.map((c) => {
              const gi = grpInfo(c.profile);
              const active = c.id === selId;
              return (
                <Box key={c.id} onClick={() => setSelId(c.id)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.5, py: 1, cursor: 'pointer',
                    borderBottom: `1px solid ${alpha(t.border, 0.5)}`, bgcolor: active ? alpha(t.primary, 0.1) : 'transparent',
                    '&:hover': { bgcolor: active ? alpha(t.primary, 0.12) : alpha(t.primary, 0.05) } }}>
                  <ThumbStack post={c} size={34} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.82rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {capText(c) || '(sans texte)'}
                    </Typography>
                  </Box>
                  <Box sx={{ width: 90 }}>
                    {gi ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: gi.color }} />
                        <Typography sx={{ fontSize: '0.72rem', color: t.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{gi.name}</Typography>
                      </Box>
                    ) : <Typography sx={{ fontSize: '0.72rem', color: t.mutedFaint }}>—</Typography>}
                  </Box>
                  <Box sx={{ width: 50, textAlign: 'right' }}><Typography sx={{ fontSize: '0.78rem', color: t.muted }}>{c.used_count ?? 0}</Typography></Box>
                  <Box sx={{ width: 60, textAlign: 'right' }}><Typography sx={{ fontSize: '0.74rem', color: t.mutedFaint }}>{fmtLibDate(c.createdAt)}</Typography></Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* ---- right : preview ---- */}
        <Box sx={{ width: 340, flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
          {!selected ? (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: t.mutedFaint, px: 3, textAlign: 'center' }}>
              <Box sx={{ width: 64, height: 64, borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha(t.primary, 0.12), mb: 1.5 }}>
                <ReplyIcon sx={{ fontSize: 28, color: t.primary }} />
              </Box>
              <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: t.foreground }}>Sélectionne un post</Typography>
              <Typography sx={{ fontSize: '0.78rem' }}>Clique pour voir l'aperçu</Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5 }}>
                {capThumb(selected) && (
                  <Box component="img" src={capThumb(selected)} sx={{ width: '100%', borderRadius: 2, mb: 1.5, border: `1px solid ${t.border}` }} />
                )}
                <Typography sx={{ fontSize: '0.88rem', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{capText(selected) || '(sans texte)'}</Typography>
                <Box sx={{ display: 'flex', gap: 2, mt: 2, color: t.mutedFaint, fontSize: '0.72rem' }}>
                  <span>Utilisé : {selected.used_count ?? 0}</span>
                  <span>{fmtLibDate(selected.createdAt)}</span>
                </Box>
              </Box>
              <Box sx={{ p: 2, borderTop: `1px solid ${t.border}` }}>
                <Button fullWidth variant="contained" startIcon={<DoneAllIcon sx={{ fontSize: 18 }} />} onClick={() => onLoad(selected)} sx={{ textTransform: 'none' }}>
                  Charger ce post
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Dialog>
  );
}

// Petit onglet de groupe (style pilule) pour le filtre de la library.
function GroupTab({ active, onClick, label, count, color }) {
  return (
    <Box role="button" onClick={onClick}
      sx={{ display: 'flex', alignItems: 'center', gap: 0.6, px: 1.25, py: 0.5, borderRadius: 999, cursor: 'pointer',
        bgcolor: active ? alpha(t.primary, 0.18) : alpha('#fff', 0.04), border: `1px solid ${active ? alpha(t.primary, 0.5) : t.border}`,
        '&:hover': { bgcolor: alpha(t.primary, 0.1) } }}>
      {color && <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color }} />}
      <Typography sx={{ fontSize: '0.76rem', fontWeight: active ? 700 : 500, color: active ? '#c7d2fe' : t.muted }}>{label}</Typography>
      <Typography sx={{ fontSize: '0.7rem', color: t.mutedFaint }}>{count}</Typography>
    </Box>
  );
}

// Modale "Post Library" du Smart Post : choisir un sous-ensemble précis de
// légendes (recherche + filtre groupe + tout sélectionner + aperçu). La
// sélection est validée sur "Done".
function CaptionPicker({ open, onClose, captions, groupList, canonGroup, initial, onApply }) {
  const [sel, setSel] = useState(new Set());
  const [search, setSearch] = useState('');
  const [grp, setGrp] = useState('all');
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (open) { setSel(new Set(initial)); setSearch(''); setGrp('all'); setPreview(null); }
  }, [open, initial]);

  const capText = (c) => c.message || c.text || '';
  const capThumb = (c) => {
    const ids = c.imageUploadIds || c.uploadIds;
    if (Array.isArray(ids) && ids.length) return `${API}/api/uploads/${ids[0]}/raw?token=${getToken()}`;
    if (c.videoData?.uploadId) return `${API}/api/uploads/${c.videoData.uploadId}/raw?token=${getToken()}`;
    return null;
  };
  const grpInfo = (profile) => groupList.find((g) => canonGroup(g.name) === canonGroup(profile)) || null;

  const filtered = useMemo(() => captions.filter((c) => {
    if (grp !== 'all' && canonGroup(c.profile) !== canonGroup(grp)) return false;
    if (search.trim() && !capText(c).toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [captions, grp, search, canonGroup]);

  const allSel = filtered.length > 0 && filtered.every((c) => sel.has(c.id));
  const toggleAll = () => setSel((prev) => {
    const n = new Set(prev);
    if (allSel) filtered.forEach((c) => n.delete(c.id));
    else filtered.forEach((c) => n.add(c.id));
    return n;
  });
  const toggle = (id) => setSel((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const done = () => { onApply(new Set(sel)); onClose(); };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { bgcolor: '#0d0d0f', border: `1px solid ${t.border}`, borderRadius: 3, height: '82vh' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 3, pt: 2.5, pb: 1.5 }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: '1.05rem', fontWeight: 700 }}>Post Library</Typography>
          <Typography sx={{ fontSize: '0.76rem', color: t.mutedFaint }}>{sel.size} légende(s) sélectionnée(s)</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: t.mutedFaint }}><CloseIcon /></IconButton>
      </Box>

      <Box sx={{ px: 3, display: 'flex', gap: 1.25, mb: 1.25 }}>
        <TextField
          fullWidth size="small" placeholder="Rechercher une légende…" value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: t.mutedFaint }} /></InputAdornment> }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <Select value={grp} onChange={(e) => setGrp(e.target.value)} displayEmpty>
            <MenuItem value="all">Tous les groupes</MenuItem>
            {groupList.map((g) => <MenuItem key={g.id} value={g.name}>{g.name}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ px: 3, mb: 1 }}>
        <Button fullWidth variant="outlined" startIcon={<DoneAllIcon sx={{ fontSize: 18 }} />} onClick={toggleAll}
          sx={{ borderColor: t.border, color: t.muted, textTransform: 'none', justifyContent: 'center' }}>
          {allSel ? `Tout désélectionner (${filtered.length})` : `Tout sélectionner (${filtered.length})`}
        </Button>
        <Typography sx={{ fontSize: '0.72rem', color: t.mutedFaint, textAlign: 'right', mt: 0.5 }}>{filtered.length} légende(s)</Typography>
      </Box>

      <DialogContent sx={{ px: 3, py: 0, borderTop: `1px solid ${t.border}` }}>
        {filtered.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center', color: t.mutedFaint }}>
            <LibraryIcon sx={{ fontSize: 36, opacity: 0.4, mb: 1 }} />
            <Typography sx={{ fontSize: '0.85rem' }}>Aucune légende{search || grp !== 'all' ? ' pour ce filtre' : ' dans la library'}.</Typography>
          </Box>
        ) : filtered.map((c) => {
          const gi = grpInfo(c.profile);
          const checked = sel.has(c.id);
          return (
            <Box key={c.id} onClick={() => toggle(c.id)}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, borderBottom: `1px solid ${alpha(t.border, 0.6)}`, cursor: 'pointer',
                '&:hover': { bgcolor: alpha(t.primary, 0.05) } }}>
              <Checkbox size="small" checked={checked} onChange={() => toggle(c.id)} onClick={(e) => e.stopPropagation()} sx={{ p: 0.5 }} />
              <ThumbStack post={c} size={34} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.82rem', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {capText(c) || '(sans texte)'}
                </Typography>
                {gi && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: gi.color }} />
                    <Typography sx={{ fontSize: '0.68rem', color: t.mutedFaint }}>{gi.name}</Typography>
                  </Box>
                )}
              </Box>
              <Tooltip title="Aperçu">
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); setPreview(c); }} sx={{ color: t.mutedFaint }}>
                  <VisibilityIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
          );
        })}
      </DialogContent>

      <Box sx={{ px: 3, py: 2, borderTop: `1px solid ${t.border}`, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" onClick={done} sx={{ px: 4, textTransform: 'none' }}>Done</Button>
      </Box>

      {/* aperçu plein texte + média */}
      <Dialog open={!!preview} onClose={() => setPreview(null)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { bgcolor: '#0d0d0f', border: `1px solid ${t.border}`, borderRadius: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2.5, pt: 2, pb: 1 }}>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, flex: 1 }}>Aperçu</Typography>
          <IconButton size="small" onClick={() => setPreview(null)} sx={{ color: t.mutedFaint }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
        </Box>
        <DialogContent sx={{ pt: 0 }}>
          {preview && capThumb(preview) && (
            <Box component="img" src={capThumb(preview)} sx={{ width: '100%', borderRadius: 2, mb: 1.5, border: `1px solid ${t.border}` }} />
          )}
          <Typography sx={{ fontSize: '0.85rem', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{preview && (capText(preview) || '(sans texte)')}</Typography>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

// Modale de sélection du Sticky post : soit on pioche dans la Post Library,
// soit on compose un post (texte + image) à la manière du Basic.
function StickyPicker({ open, onClose, captions, groupColor, groupList = [], canonGroup = (x) => x, onChoose, notify }) {
  const [tab, setTab] = useState('library');
  const [text, setText] = useState('');
  const [images, setImages] = useState([]); // {id, preview}
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // filtres de la liste library (alignés avec les autres modales)
  const [search, setSearch] = useState('');
  const [grp, setGrp] = useState('all');
  const [typeF, setTypeF] = useState('all'); // all | text | image | video

  useEffect(() => {
    if (open) { setTab('library'); setText(''); setImages([]); setSearch(''); setGrp('all'); setTypeF('all'); }
  }, [open]);

  const groupCounts = useMemo(() => {
    const m = {};
    captions.forEach((c) => { const k = canonGroup(c.profile); if (k) m[k] = (m[k] || 0) + 1; });
    return m;
  }, [captions, canonGroup]);

  const filteredLib = useMemo(() => captions.filter((c) => {
    if (grp !== 'all' && canonGroup(c.profile) !== canonGroup(grp)) return false;
    if (typeF !== 'all' && mediaTypeOf(c) !== typeF) return false;
    if (search.trim() && !(c.message || '').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [captions, grp, typeF, search, canonGroup]);

  const TYPES = [['all', 'All'], ['text', 'Aa'], ['image', 'Img'], ['video', 'Vid']];

  const textRef = useRef(null);
  const uploadUrl = (id) => `${API}/api/uploads/${id}/raw?token=${getToken()}`;

  const insertVar = (key) => {
    const el = textRef.current;
    const token = `{${key}}`;
    if (!el) { setText((m) => m + token); return; }
    const start = el.selectionStart ?? text.length;
    const end = el.selectionEnd ?? text.length;
    setText(text.slice(0, start) + token + text.slice(end));
    requestAnimationFrame(() => { el.focus(); el.selectionStart = el.selectionEnd = start + token.length; });
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('image', file);
        const { data } = await axios.post(`${API}/api/upload`, fd);
        setImages((prev) => [...prev, { id: data.uploadId, preview: URL.createObjectURL(file) }]);
      }
    } catch (err) { notify && notify("Erreur d'upload", 'error'); }
    finally { setUploading(false); }
  };

  const chooseLibrary = (c) => {
    const ids = Array.isArray(c.imageUploadIds) ? c.imageUploadIds : [];
    onChoose({
      message: c.message || '',
      mediaType: c.mediaType || (ids.length ? 'image' : 'text'),
      imageUploadIds: ids,
      randomFolder: c.randomFolder || 0,
      randomCount: c.randomCount || 0,
      preview: ids.length ? uploadUrl(ids[0]) : null,
      source: 'library',
    });
  };

  const chooseCompose = () => {
    if (!text.trim() && images.length === 0) { notify && notify('Post vide', 'error'); return; }
    onChoose({
      message: text,
      mediaType: images.length ? 'image' : 'text',
      imageUploadIds: images.map((i) => i.id),
      preview: images.length ? images[0].preview : null,
      source: 'compose',
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { bgcolor: t.card, border: `1px solid ${t.border}`, borderRadius: 3 } }}>
      <DialogContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PinIcon sx={{ fontSize: 20, color: '#ec4899' }} />
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 700 }}>Choisir le Sticky post</Typography>
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ color: t.muted }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
        </Box>

        <ToggleButtonGroup exclusive fullWidth size="small" value={tab} onChange={(_, v) => v && setTab(v)} sx={{ ...tgStyle, mb: 2 }}>
          <ToggleButton value="library"><LibraryIcon sx={{ fontSize: 16, mr: 0.5 }} /> Post Library</ToggleButton>
          <ToggleButton value="compose"><EditIcon sx={{ fontSize: 16, mr: 0.5 }} /> Composer</ToggleButton>
        </ToggleButtonGroup>

        {tab === 'library' ? (
          <Box>
            {/* recherche */}
            <TextField size="small" fullWidth placeholder="Rechercher une légende…" value={search} onChange={(e) => setSearch(e.target.value)}
              sx={{ mb: 1.25 }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: t.mutedFaint }} /></InputAdornment> }} />

            {/* onglets de groupe */}
            {groupList.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>
                <GroupTab active={grp === 'all'} onClick={() => setGrp('all')} label="Tous" count={captions.length} />
                {groupList.map((g) => (
                  <GroupTab key={g.id} active={canonGroup(grp) === canonGroup(g.name)} onClick={() => setGrp(g.name)}
                    label={g.name} count={groupCounts[canonGroup(g.name)] || 0} color={g.color} />
                ))}
              </Box>
            )}

            {/* filtre type */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
              <ToggleButtonGroup exclusive size="small" value={typeF} onChange={(_, v) => v && setTypeF(v)} sx={tgStyle}>
                {TYPES.map(([v, l]) => <ToggleButton key={v} value={v} sx={{ px: 1.25 }}>{l}</ToggleButton>)}
              </ToggleButtonGroup>
              <Typography sx={{ fontSize: '0.72rem', color: t.mutedFaint, ml: 1.5, whiteSpace: 'nowrap' }}>{filteredLib.length} post{filteredLib.length > 1 ? 's' : ''}</Typography>
            </Box>

            <Box sx={{ maxHeight: 340, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {captions.length === 0 && (
              <Typography sx={{ fontSize: '0.85rem', color: t.mutedFaint, py: 3, textAlign: 'center' }}>
                La Post Library est vide. Compose un post dans l'onglet « Composer ».
              </Typography>
            )}
            {captions.length > 0 && filteredLib.length === 0 && (
              <Typography sx={{ fontSize: '0.85rem', color: t.mutedFaint, py: 3, textAlign: 'center' }}>
                Aucun post pour ce filtre.
              </Typography>
            )}
            {filteredLib.map((c) => {
              return (
                <Box key={c.id} onClick={() => chooseLibrary(c)}
                  sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, p: 1.25, borderRadius: 2, cursor: 'pointer',
                    border: `1px solid ${t.border}`, bgcolor: t.background, transition: 'border-color .15s',
                    '&:hover': { borderColor: '#ec4899', bgcolor: alpha('#ec4899', 0.06) } }}>
                  {c.randomFolder ? (
                    <Box sx={{ width: 42, height: 42, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha('#f43f5e', 0.15), flexShrink: 0 }}>🎲</Box>
                  ) : (
                    <ThumbStack post={c} size={42} />
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.82rem', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {c.message || '(sans texte)'}
                    </Typography>
                    {c.profile && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.4 }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: groupColor(c.profile) || t.mutedFaint }} />
                        <Typography sx={{ fontSize: '0.68rem', color: t.mutedFaint }}>{c.profile}</Typography>
                      </Box>
                    )}
                  </Box>
                  <CheckCircleIcon sx={{ fontSize: 18, color: t.mutedFaint, opacity: 0.4, flexShrink: 0 }} />
                </Box>
              );
            })}
            </Box>
          </Box>
        ) : (
          <Box>
            <Box sx={{ border: `1px solid ${t.border}`, borderRadius: 2.5, bgcolor: t.background, p: 1.5 }}>
              <TextField inputRef={textRef} fullWidth multiline minRows={5} maxRows={12} variant="standard"
                placeholder="Quoi de neuf ?" value={text}
                onChange={(e) => setText(e.target.value.slice(0, 500))}
                InputProps={{ disableUnderline: true, sx: { fontSize: '0.95rem' } }} />
              {images.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                  {images.map((img, i) => (
                    <Box key={i} sx={{ position: 'relative' }}>
                      <Box component="img" src={img.preview} sx={{ width: 56, height: 56, borderRadius: 1.5, objectFit: 'cover', border: `1px solid ${t.border}` }} />
                      <IconButtonClose onClick={() => setImages((p) => p.filter((_, idx) => idx !== i))} />
                    </Box>
                  ))}
                </Box>
              )}
              <Divider sx={{ my: 1.25, borderColor: t.border }} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={handleUpload} />
                <Tooltip title="Ajouter des images">
                  <Box component="span" onClick={() => fileRef.current && fileRef.current.click()} sx={iconBtn}>
                    {uploading ? <CircularProgress size={18} /> : <AddPhotoIcon sx={{ fontSize: 19 }} />}
                  </Box>
                </Tooltip>
                <Typography sx={{ fontSize: '0.78rem', color: text.length > 450 ? '#fbbf24' : t.mutedFaint }}>{500 - text.length}</Typography>
              </Box>
            </Box>

            {/* variables */}
            <Box sx={{ mt: 1.75 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
                <Typography sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.74rem', color: t.mutedFaint }}>{'{}'}</Typography>
                <Typography sx={{ fontSize: '0.74rem', color: t.muted, fontWeight: 600 }}>Variables</Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                {VARIABLES.map((v) => (
                  <Chip key={v} label={v} size="small" onClick={() => insertVar(v)}
                    sx={{ bgcolor: alpha('#fff', 0.04), border: `1px solid ${t.border}`, color: t.muted, fontFamily: 'JetBrains Mono, monospace', fontSize: '0.7rem', cursor: 'pointer', '&:hover': { bgcolor: alpha(t.primary, 0.15), color: '#c7d2fe' } }} />
                ))}
              </Box>
              <Typography sx={{ fontSize: '0.7rem', color: t.mutedFaint, mt: 0.75 }}>Résolues automatiquement selon le compte cible.</Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button variant="contained" startIcon={<PinIcon sx={{ fontSize: 16 }} />} onClick={chooseCompose}
                sx={{ bgcolor: '#ec4899', '&:hover': { bgcolor: '#db2777' } }}>
                Définir comme Sticky
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

const tgStyle = {
  bgcolor: '#0a0a0c', p: 0.4, borderRadius: 2, width: '100%',
  '& .MuiToggleButton-root': { border: 0, borderRadius: '6px !important', py: 0.5, textTransform: 'none', color: '#a1a1aa', flex: 1 },
  '& .Mui-selected': { bgcolor: '#ffffff14 !important', color: '#fafafa !important' },
};

const iconBtn = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32,
  borderRadius: 1.5, cursor: 'pointer', color: t.muted, transition: 'background-color .15s',
  '&:hover': { backgroundColor: alpha('#fff', 0.06), color: t.foreground },
};

function Label({ icon, text, help }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75, color: t.muted }}>
      {icon}
      <Typography sx={{ fontSize: '0.78rem', fontWeight: 500 }}>{text}</Typography>
      {help && <HelpIcon sx={{ fontSize: 13, color: t.mutedFaint }} />}
    </Box>
  );
}

function OptionToggle({ icon, title, sub, checked, onChange, disabled }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: 1.25, borderRadius: 2, bgcolor: t.background, border: `1px solid ${t.border}`, opacity: disabled ? 0.55 : 1 }}>
      <Box sx={{ color: checked ? t.primary : t.mutedFaint, display: 'flex' }}>{icon}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{title}</Typography>
        <Typography noWrap sx={{ fontSize: '0.72rem', color: t.mutedFaint }}>{sub}</Typography>
      </Box>
      <Switch size="small" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
    </Box>
  );
}

function IconButtonClose({ onClick }) {
  return (
    <IconButton size="small" onClick={onClick}
      sx={{ position: 'absolute', top: -8, right: -8, bgcolor: t.background, border: `1px solid ${t.border}`, p: 0.25, '&:hover': { bgcolor: '#000' } }}>
      <CloseIcon sx={{ fontSize: 13 }} />
    </IconButton>
  );
}
